import LoadingIndicator from "@/components/ui/loading/LoadingIndicator";
import { dateFormat } from "@/lib/utils";
import IBorrowingData from "@/models/borrowing/BorrowingData";
import { ReactNode, useEffect } from "react";

type ActionProps = {
  children: ReactNode;
};

type FetchedMaterial = {
  id: number;
  name: string;
  requestAmount: number;
  unit: string;
};

const BorrowingDetailMaterials = ({ materials, isLoading }: { materials: FetchedMaterial[], isLoading?: boolean }) => {
  if (isLoading) {
    return (
      <div className="mt-6 flex flex-col gap-2 rounded-lg border bg-secondary/30 p-5 shadow-sm animate-pulse">
        <div className="h-4 w-40 bg-muted-foreground/20 rounded"></div>
        <div className="space-y-3 mt-4">
          <div className="flex items-center justify-between border-b border-border/50 pb-3">
            <div className="h-4 w-32 bg-muted-foreground/20 rounded"></div>
            <div className="h-6 w-16 bg-muted-foreground/20 rounded"></div>
          </div>
          <div className="flex items-center justify-between border-b border-border/50 pb-3">
            <div className="h-4 w-24 bg-muted-foreground/20 rounded"></div>
            <div className="h-6 w-16 bg-muted-foreground/20 rounded"></div>
          </div>
        </div>
      </div>
    );
  }
  
  if (!materials || materials.length === 0) return null;

  return (
    <div className="mt-6 flex flex-col gap-2 rounded-lg border bg-secondary/30 p-5 shadow-sm">
      <h4 className="text-sm font-semibold tracking-wide text-foreground uppercase">
        Requested Materials
      </h4>
      <ul className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4 mt-2 text-sm text-muted-foreground">
        {materials.map((mat) => (
          <li
            key={mat.id}
            className="flex items-center justify-between border border-border/50 rounded-md p-3 bg-background shadow-sm"
          >
            <span className="font-medium text-foreground truncate mr-2" title={mat.name}>{mat.name}</span>
            <span className="shrink-0 rounded-md bg-secondary px-3 py-1 font-semibold text-foreground border shadow-sm">
              {mat.requestAmount} <span className="text-xs text-muted-foreground uppercase">{mat.unit}</span>
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
};

const BorrowingDetailActions = (props: ActionProps) => {
  return (
    <div className="flex flex-row justify-end py-4">
      <div className="flex flex-row-reverse gap-4">{props.children}</div>
    </div>
  );
};

type Props = {
  data: IBorrowingData | undefined;
  children: ReactNode;
};

export const extractShiftNames = (shifts: any): string => {
  const shiftNumbers = shifts
    .map((shift: any) => {
      const match = shift.name.match(/^(Shift (\d+))/);
      return match ? match[2] : "";
    })
    .filter((num: any) => num !== "");

  if (shiftNumbers.length === 0) {
    return "";
  }

  // Add "Shift" in front of the first shift number and join the rest with commas
  return `Shift ${shiftNumbers[0]}${
    shiftNumbers.length > 1 ? ", " + shiftNumbers.slice(1).join(", ") : ""
  }`;
};

const BorrowingDetail = (props: Props) => {
  const data = props.data;
  const processedByLabel = data?.processed_by_user
    ? `${data.processed_by_user.initial} - ${data.processed_by_user.name}`
    : "-";
  const finalizedByLabel = data?.finalized_by_user
    ? `${data.finalized_by_user.initial} - ${data.finalized_by_user.name}`
    : "-";

  const getStatusColor = (status: string) => {
    switch (status) {
      case "Accepted":
        return "text-green-600";
      case "Finalized":
        return "text-green-600";
      case "Rejected":
        return "text-red-600";
      case "Pending":
        return "text-muted-foreground";
      case "Canceled":
        return "text-red-600";
      default:
        break;
    }
  };

  useEffect(() => {
    if (data)
      console.log(
        data.is_private_borrowing,
        data.userData.borrower_count,
        data.room.capacity
      );
  }, [data]);

  return (
    <>
      {data ? (
        <div className="flex-col px-6 pb-4 pt-12 w-full">
          <div className="text-xl font-bold">{data.title}</div>
          <div className="text-sm font-semibold text-green-600 pb-4">
            {data.id > 0 ? <>ID {data.id}</> : <>New</>}
          </div>
          <section className="flex flex-col pb-4 lg:flex-row w-full gap-4 items-start lg:h-[17rem] h-auto">
            <div className="w-full flex flex-col flex-1 gap-1 p-4 rounded-lg bg-secondary min-w-fit min-h-fit h-auto lg:h-full lg:w-auto">
              <div className="text-lg font-semibold">
                Room : {data.room.room_code}
              </div>
              <div className="text-muted-foreground text-sm">
                {data.room.room_name ?? ""}
              </div>
              <div className="text-muted-foreground text-sm">
                {data.room.type?.name}
              </div>
              <div className="grid columns-2"></div>
              <div className="text-sm grid grid-cols-1 lg:grid-cols-2 w-fit gap-x-2 gap-y-1">
                <div>Borrowing Date</div>
                <div className="text-blue-500 font-semibold">
                  {dateFormat(data.date)} (
                  {extractShiftNames(data?.detail_shifts)})
                </div>
                <div>Request Date</div>
                <div className="text-orange-500 font-semibold">
                  {data.created_at === ""
                    ? dateFormat(new Date())
                    : dateFormat(data.created_at)}
                </div>
                <div>Status</div>
                <div
                  className={`${getStatusColor(
                    data.borrowing_status
                  )} font-semibold`}
                >
                  {data.borrowing_status}
                </div>
                {data?.reason && (
                  <>
                    <div>Reason</div>
                    <div className="text-muted-foreground lg:max-w-[14rem] lg:max-h-[4rem] overflow-hidden text-ellipsis">
                      {data.reason}
                    </div>
                  </>
                )}
                {data.room.room_privacy_category === "Mixed" && (
                  <>
                    <div>Borrowing Type</div>
                    <div className="font-semibold">
                      {data.is_private_borrowing === true ||
                      data.userData.borrower_count === data.room.capacity
                        ? "Private"
                        : "Open"}
                    </div>
                  </>
                )}
              </div>
            </div>
            <div className="flex flex-col gap-1 p-4 rounded-lg bg-secondary w-full lg:w-auto lg:h-full overflow-auto">
              <div className="text-lg font-semibold">Team Members</div>
              {data.detail_borrowings.length === 0 ? (
                <div>No Team Member</div>
              ) : (
                data.detail_borrowings?.map((d, i) => (
                  <div
                    className="flex gap-1 items-center font-semibold"
                    key={i}
                  >
                    <div>{i + 1}. </div>
                    <div
                      className={`text-sm ${
                        d.student_nim === data.student_nim_leader &&
                        "text-green-500"
                      }`}
                      key={d.id}
                    >
                      {d.student_nim}
                      {" - "} {d.student_name}{" "}
                      {d.student_nim === data.student_nim_leader && (
                        <>(Leader)</>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
            <div className="flex flex-col max-h-[17rem] lg:max-h-full lg:h-full overflow-auto w-full flex-1 gap-1 p-4 rounded-lg bg-secondary">
              <div className="text-lg font-semibold">
                Assets : {data.assets?.length}
              </div>
              {data.assets?.map((a) => (
                <div key={a.id} className="flex flex-row gap-4 py-4">
                  <img
                    src={
                      a.images && a.images.length > 0
                        ? a.images[0].path
                        : "https://picsum.photos/200/300"
                    }
                    className="h-24 w-24"
                    alt="image not found"
                  />
                  <div className="flex flex-col">
                    <div className="text-sm font-semibold">
                      {a.name} - {a.id}
                    </div>
                    <div className="text-sm">{a.description}</div>
                    <div className="text-sm">{a.year}</div>
                  </div>
                </div>
              ))}

              {!data.assets ||
                (data.assets.length < 1 && (
                  <div className="w-[14rem]">No Assets</div>
                ))}
            </div>
          </section>
          <section className="flex flex-col flex-1 gap-1 p-4 rounded-lg bg-secondary w-full lg:w-auto min-h-60">
            <div className="text-lg font-semibold">
              Borrower : {data.student_nim_leader}
            </div>
            <div className="text-sm grid grid-cols-1 lg:grid-cols-[150px_auto] gap-y-1">
              <div>Email</div>
              <div className="text-muted-foreground">{data.userData.email}</div>
              <div>Phone</div>
              <div className="text-muted-foreground">{data.userData.phone}</div>
              <div>Category</div>
              <div className="text-muted-foreground">
                {data.userData.borrowing_reason_category ?? "-"}
              </div>
              <div>Notes</div>
              <div className="text-muted-foreground">
                {data.userData.purpose}
              </div>
              <div>Internet</div>
              <div className="text-muted-foreground">
                {!data.need_internet ? "No" : "Yes"}
              </div>
              <div>PC Count</div>
              <div className="text-muted-foreground">
                {data.pc_count || 0} PCs
              </div>
              <div>Processed By</div>
              <div className="text-muted-foreground">{processedByLabel}</div>
              <div>Finalized By</div>
              <div className="text-muted-foreground">{finalizedByLabel}</div>
            </div>
          </section>
          {props.children}
        </div>
      ) : (
        <LoadingIndicator loading={true} />
      )}
    </>
  );
};

export { BorrowingDetail, BorrowingDetailActions, BorrowingDetailMaterials, type FetchedMaterial };
